/**
 * level1Session.ts
 * Per-level session helpers for World 1.
 *
 * A single `getWorld1Session()` call returns everything `app/game.tsx`
 * needs to boot any World 1 stage (normal levels 1–4 plus secret stage
 * 1-0). The screen reads `?level=` from the router and routes the result
 * through this helper — no more hard-coded `LEVEL_1_1_*` constants.
 *
 * @module level1Session
 */

import { getLevel } from './LevelRegistry';
import type { TileMapResult } from './TileMapLoader';
import type { PlayScene } from '../scenes/PlayScene';
import { gameState } from '../engine/gameState';
import SaveManager from '../services/saveManager';
import Logger from '../utils/logger';
import { mergeIds } from './mergeIds';

// Re-export mergeIds from its side-effect-free module so existing imports
// continue to work.
export { mergeIds };

/** Shape returned by {@link getWorld1Session}. */
export interface World1Session {
  /** Save-key identifier ('1-1', '1-2', '1-3', '1-4', '1-0'). */
  readonly id: string;
  /** Underlying parsed tile-map result (runtime, checkpoints, spawns, …). */
  readonly tileMap: TileMapResult;
  /** Optional finale config — only level 1-1 has the finale sequence. */
  readonly finaleConfig?: { gateCol: number; rampCol: number };
}

const ID_TO_LEVEL_NUMBER: Record<string, number> = {
  '1-1': 1,
  '1-2': 2,
  '1-3': 3,
  '1-4': 4,
  // DECISION: Secret stage canonical id is '1-0' to match LevelRegistry's
  // numeric convention (levelId 0 = secret). Avoids divergent save keys.
  '1-0': 0,
};

// DECISION: Levels 1-1 and 1-3 ship the finale sequence (portal gate + ramp).
// 1-3 was added in the world1-3-redesign so its end sequence matches the
// "walk into the portal" verb established by 1-1. Other World 1 levels exit
// on right-edge collision (see physicsStep.ts).
//
// DECISION: 1-3's rampCol must satisfy `rampCol*16 + (RAMP_W - SHIP_FRAME_W)/2
// + SHIP_FRAME_W <= levelWidth` so the parked ship sprite stays inside the
// level during BOARDING/IGNITION. For a 320-tile (5120px) level with
// RAMP_W=80 and SHIP_FRAME_W=128, that caps rampCol at 313. We use 308 to
// match 1-1's 88px right-margin cadence (450w → rampCol 438 → 88px margin).
const FINALE_CONFIG_BY_ID: Record<string, { gateCol: number; rampCol: number }> = {
  '1-1': { gateCol: 432, rampCol: 438 },
  '1-3': { gateCol: 302, rampCol: 308 },
};

/**
 * Resolve a `?level=` query value to a canonical World 1 session id.
 * Falls back to '1-1' for any unknown or missing input so the screen
 * never boots into an unregistered level.
 *
 * @param raw - Raw param value from `useLocalSearchParams`.
 * @returns A valid session id string.
 */
export function resolveWorld1LevelId(raw: string | string[] | undefined): string {
  const candidate = Array.isArray(raw) ? raw[0] : raw;
  // DECISION: Object.prototype.hasOwnProperty.call so prototype keys like
  // 'toString' or '__proto__' don't sneak through the `in` operator.
  if (
    candidate !== undefined &&
    Object.prototype.hasOwnProperty.call(ID_TO_LEVEL_NUMBER, candidate)
  ) {
    return candidate;
  }
  return '1-1';
}

/**
 * Build a session bundle for the given World 1 level id.
 *
 * @param levelId - Canonical id ('1-1', '1-2', '1-3', '1-4', '1-0').
 * @returns A World1Session with everything `scene.init` needs.
 * @throws If the level id is not registered (callers should validate via
 *   {@link resolveWorld1LevelId} first).
 */
export function getWorld1Session(levelId: string): World1Session {
  // DECISION: Own-property + typeof check — guards against prototype keys
  // ('toString', '__proto__') sneaking past plain-object indexing.
  if (!Object.prototype.hasOwnProperty.call(ID_TO_LEVEL_NUMBER, levelId)) {
    throw new Error(`Unknown World 1 level id: ${levelId}`);
  }
  const num = ID_TO_LEVEL_NUMBER[levelId];
  if (typeof num !== 'number') {
    throw new Error(`Unknown World 1 level id: ${levelId}`);
  }
  const tileMap = getLevel(1, num);
  if (tileMap === undefined) {
    throw new Error(`World 1 level ${levelId} not registered`);
  }
  const finaleConfig = Object.prototype.hasOwnProperty.call(FINALE_CONFIG_BY_ID, levelId)
    ? FINALE_CONFIG_BY_ID[levelId]
    : undefined;
  if (finaleConfig !== undefined) {
    return { id: levelId, tileMap, finaleConfig };
  }
  return { id: levelId, tileMap };
}

/**
 * Initialise the PlayScene from a session bundle. Handles the finale
 * wiring for 1-1 and the no-finale path for all other World 1 stages.
 *
 * @param scene - The PlayScene to initialise.
 * @param session - The session bundle returned by {@link getWorld1Session}.
 * @param canSkip - Whether the finale cinematic may be skipped (1-1 only).
 * @param skipCheckpointRestore - When true, ignore the persisted
 *   `lastCheckpointId` so the player respawns at the level start. Used by
 *   the in-session Restart button.
 */
export function initWorld1Scene(
  scene: PlayScene,
  session: World1Session,
  canSkip: boolean,
  skipCheckpointRestore = false,
): void {
  const { tileMap, finaleConfig } = session;
  scene.init(
    tileMap.levelRuntime,
    tileMap.levelData.playerSpawnX,
    tileMap.levelData.playerSpawnY,
    session.id,
    tileMap.checkpoints,
    tileMap.levelData.starSeeds,
    tileMap.levelData.constellationShards,
    tileMap.levelData.heartFragments,
    tileMap.enemySpawns,
    finaleConfig !== undefined
      ? { gateCol: finaleConfig.gateCol, rampCol: finaleConfig.rampCol, canSkip }
      : undefined,
    skipCheckpointRestore,
    tileMap.levelData.weaponPickups,
  );
}

/**
 * Return the next World 1 level id in normal-progression order, or `null`
 * if the player has just finished the final normal stage. The secret
 * stage (1-0) is intentionally excluded from the progression chain — it
 * is reached via the world map only.
 *
 * @param levelId - Current level id.
 * @returns The next level id, or `null` if there is none.
 */
export function getNextWorld1LevelId(levelId: string): string | null {
  switch (levelId) {
    case '1-1': return '1-2';
    case '1-2': return '1-3';
    case '1-3': return '1-4';
    default: return null;
  }
}

/**
 * Persist the just-completed World 1 level's collected ids and score to
 * the save file. Fire-and-forget: failures are logged, never thrown.
 *
 * @param levelId - Save key for the completed level.
 */
export async function persistWorld1Completion(levelId: string): Promise<void> {
  // DECISION: Snapshot gameState BEFORE the first await — otherwise a fast
  // Next-tap can reset gameState.collectibles/score (via PlayScene.init)
  // between load() and the read, corrupting the save payload.
  const collectedSeeds = gameState.collectibles
    .filter((c) => c.collected && c.kind === 'seed')
    .map((c) => c.id);
  const collectedShards = gameState.collectibles
    .filter((c) => c.collected && c.kind === 'shard')
    .map((c) => c.id);
  const collectedHearts = gameState.collectibles
    .filter((c) => c.collected && c.kind === 'heart_fragment')
    .map((c) => c.id);
  const scoreSnapshot = gameState.score;
  try {
    const existing = (await SaveManager.load()) ?? SaveManager.createDefaultSave();
    const prev = existing.levels[levelId];
    existing.levels[levelId] = {
      completed: true,
      collectedStarSeedIds: mergeIds(prev?.collectedStarSeedIds, collectedSeeds),
      collectedConstellationShardIds: mergeIds(prev?.collectedConstellationShardIds, collectedShards),
      collectedHeartFragmentIds: mergeIds(prev?.collectedHeartFragmentIds, collectedHearts),
      lastCheckpointId: null,
    };
    existing.totalScore = existing.totalScore + scoreSnapshot;
    await SaveManager.save(existing);
  } catch (err) {
    Logger.error('GameScreen', 'Failed to save level progress', err);
  }
}
